import {useEffect, useMemo, useState} from 'react';

export function createAudioSelection(channelUrl) {
  return {
    channelUrl,
    preferred: 'auto',
    tracks: [],
    selected: 'auto',
    listener: null,
    nativeSelect: null,
    nativeMode: false,
    publish(tracks) {
      const selected =
        this.preferred !== 'auto' && tracks.some((track) => track.id === this.preferred)
          ? this.preferred
          : 'auto';
      if (JSON.stringify([tracks, selected]) === JSON.stringify([this.tracks, this.selected]))
        return;
      this.tracks = tracks;
      this.selected = selected;
      this.listener?.({tracks, selected});
    },
    select(id) {
      if (id !== 'auto' && !this.tracks.some((track) => track.id === id)) return false;
      this.preferred = id;
      this.selected = id;
      this.listener?.({tracks: this.tracks, selected: id});
      return this.nativeSelect?.(id) || false;
    }
  };
}

export default function useAudioTracks(channelUrl, videoRef) {
  const audio = useMemo(() => createAudioSelection(channelUrl), [channelUrl]);
  const [snapshot, setSnapshot] = useState({tracks: [], selected: 'auto'});
  useEffect(() => {
    audio.listener = setSnapshot;
    setSnapshot({tracks: audio.tracks, selected: audio.selected});
    const video = videoRef.current;
    const list = video?.audioTracks;
    if (!list)
      return () => {
        audio.listener = null;
      };
    const nativeTracks = () => Array.from({length: list.length}, (_, index) => list[index]);
    const idOf = (track, index) => {
      const key = `${track.language || ''}:${track.label || ''}`;
      const ordinal = nativeTracks().slice(0, index).filter(previous =>
        `${previous.language || ''}:${previous.label || ''}` === key).length;
      return `native:${key}:${ordinal}`;
    };
    const refresh = () => {
      const tracks = nativeTracks();
      if (!audio.nativeMode || !tracks.length) return;
      audio.publish(
        tracks.map((track, index) => ({
          id: idOf(track, index),
          label: track.label || track.language || `Audio ${index + 1}`
        })),
        idOf(
          tracks.find((track) => track.enabled) || tracks[0],
          Math.max(
            0,
            tracks.findIndex((track) => track.enabled)
          )
        )
      );
    };
    audio.nativeSelect = (id) => {
      if (!audio.nativeMode) return false;
      const tracks = nativeTracks();
      const index = id === 'auto' ? 0 : tracks.findIndex((track, i) => idOf(track, i) === id);
      if (index < 0 || !tracks.length) return false;
      tracks.forEach((track, i) => {
        track.enabled = i === index;
      });
      return true;
    };
    const restore = () => {
      audio.nativeSelect(audio.preferred);
      refresh();
    };
    list.addEventListener?.('addtrack', restore);
    list.addEventListener?.('removetrack', refresh);
    list.addEventListener?.('change', refresh);
    video.addEventListener('loadedmetadata', restore);
    refresh();
    return () => {
      audio.listener = null;
      audio.nativeSelect = null;
      list.removeEventListener?.('addtrack', restore);
      list.removeEventListener?.('removetrack', refresh);
      list.removeEventListener?.('change', refresh);
      video.removeEventListener('loadedmetadata', restore);
    };
  }, [audio, videoRef]);
  return {audio, audioTracks: snapshot.tracks, selectedAudioTrack: snapshot.selected};
}
